import { api } from '@claimshield/shared'
import type { AxiosResponse } from 'axios'

export type StateAccessStatus = 'not_required' | 'not_started' | 'packet_sent' | 'client_submitted_to_state' | 'submitted_to_samba' | 'approved' | 'samba_rejected' | 'expired' | 'declined'
export interface StateAccessStep { key: string; label: string; done: boolean; current: boolean }
export interface StateAccessRow {
  state: string
  status: StateAccessStatus
  driver_count: number
  steps: StateAccessStep[]
  packet_available: boolean
  sent_to_samba_on: string | null
  consultant: { name: string; email: string } | null
  state_deadline?: string | null
  evidence_uploaded?: boolean
  renewal_proof_uploaded?: boolean
  code_entered?: boolean
  reference?: string | null
  note?: string | null
}
export interface StateAccessResponse { rows: StateAccessRow[] }
const base = '/v1/client-portal/state-access'
const tokenBase = '/v1/onboarding/state-access'
const scopedBase = (token?: string) => token === undefined ? base : tokenBase
const statePath = (state: string, token?: string) => `${scopedBase(token)}/${encodeURIComponent(state.toUpperCase())}`

export function fetchStateAccess(token?: string): Promise<AxiosResponse<StateAccessResponse>> {
  return token === undefined
    ? api.get<StateAccessResponse>(base)
    : api.get<StateAccessResponse>(tokenBase, { params: { token } })
}
export async function progress(state: string, options: { reference?: string; code?: string } = {}, token?: string): Promise<AxiosResponse<StateAccessRow>> {
  const body: { reference?: string; code?: string } = {}
  if (options.reference?.trim()) body.reference = options.reference.trim()
  if (options.code?.trim()) {
    if (state.toUpperCase() !== 'CA') throw new Error('Requester code applies only to California.')
    body.code = options.code.trim()
  }
  const url = `${statePath(state, token)}/progress`
  return token === undefined ? api.post<StateAccessRow>(url, body) : api.post<StateAccessRow>(url, body, { params: { token } })
}
export async function upload(state: string, file: File, token?: string): Promise<AxiosResponse<StateAccessRow>> {
  if (!file.size) throw new Error('Choose a nonempty signed document.')
  if (!['application/pdf', 'image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
    throw new Error('Choose a PDF or image file.')
  }
  const form = new FormData()
  form.append('file', file)
  const url = `${statePath(state, token)}/upload`
  return token === undefined ? api.post<StateAccessRow>(url, form) : api.post<StateAccessRow>(url, form, { params: { token } })
}
export async function downloadPacket(state: string, token?: string): Promise<void> {
  try {
    const response = await api.get<Blob>(`${statePath(state, token)}/packet`, {
      responseType: 'blob', ...(token === undefined ? {} : { params: { token } }),
    })
    const disposition = String(response.headers?.['content-disposition'] ?? '')
    const name = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i)
    const filename = name ? decodeURIComponent(name[1] ?? name[2] ?? '') : `${state.toLowerCase()}-state-access-packet.zip`
    const url = URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.append(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  } catch (error: any) {
    if (error?.response?.data instanceof Blob && error.response.data.type.includes('json')) {
      const json = JSON.parse(await error.response.data.text())
      throw new Error(typeof json.detail === 'string' ? json.detail : 'Packet download failed.')
    }
    throw error
  }
}
